/**
 * @file Appointment booking links — issue / list / revoke, plus the patient-side context read.
 * @module api/healthcare/BookingLinkApi
 *
 * S8 of ClickUp 86d2w0fwz. The practitioner half of the tokenised booking surface healthcare's
 * `AppointmentBookingLinkController` exposes:
 *
 *   POST   practitioner/appointments/booking-links             → issueBookingLink
 *   GET    practitioner/appointments/booking-links?patient_id=  → listBookingLinks
 *   POST   practitioner/appointments/booking-links/{id}/revoke  → revokeBookingLink
 *   GET    patient/bookings/link                                → getBookingLinkContext
 *
 * ⚠️ EVERY ISSUE MINTS A NEW LINK. Deliberately unlike `issueFormLink`, which is idempotent
 * because one assignment has one canonical link. A booking link is PER-SEND: a second link must
 * not silently invalidate the first, and each is independently revocable. A caller that issues
 * twice has handed out two live capabilities — surface which one is on screen.
 *
 * 🚨 `issueBookingLink` IS THE ONLY RESPONSE THAT CARRIES A TOKEN. `listBookingLinks` and
 * `revokeBookingLink` are built from an explicit server-side whitelist and never do; do not add a
 * type that implies otherwise. The token is a live capability to book as a named patient — it is
 * never logged, never stored, and only ever rendered as part of the whole URL
 * (`utils/healthcare/bookingLink`).
 *
 * ⚠️ `getBookingLinkContext` is the ONE function here that is NOT practitioner-authenticated — it
 * runs on the patient's device, where there is usually no session at all and the link token IS the
 * credential. It is therefore **re-exported from `AppointmentApi`, not reimplemented here**: S9
 * (ClickUp 86d3yhdw9) landed it there alongside `createAppointmentViaLink`, and the patient
 * booking page already imports it from there. Two copies of one tokenised endpoint is exactly the
 * drift that gets a transport detail wrong on the half nobody is looking at — see that function's
 * docblock for how the link token travels.
 */
import { ENV } from "../../../config/runtimeEnv";
import type { UserUuid } from "../../../types/identity";
import { http } from "../../../lib/http/client";

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

/** Derived server-side from `expires_at` / `consumed_at` / `revoked_at` — never stored. */
export type BookingLinkStatus = "active" | "expired" | "used" | "revoked";

/** The 201 from `issueBookingLink` — the only shape in this module that carries a token. */
export interface IssuedBookingLink {
	id: number;
	token: string;
	expires_at: string | null;
	appointment_type_id: number | null;
	practitioner_id: UserUuid | null;
}

/** The practitioner-facing shape of a link. Tokenless, by construction, on the server. */
export interface BookingLink {
	id: number;
	patient_id: UserUuid;
	appointment_type_id: number | null;
	practitioner_id: UserUuid | null;
	issued_by_user_id: UserUuid;
	status: BookingLinkStatus;
	expires_at: string | null;
	consumed_at: string | null;
	revoked_at: string | null;
	appointment_id: number | null;
	created_at: string | null;
}

/**
 * The patient-side context read, re-exported so this module is the one place a caller has to look
 * for the booking-link surface — but implemented ONCE, in `AppointmentApi`, beside the POST that
 * consumes the link. See this file's header.
 */
export { getBookingLinkContext } from "./AppointmentApi";
export type { BookingLinkContext } from "./AppointmentApi";

export interface IssueBookingLinkInput {
	patientId: UserUuid;
	appointmentTypeId?: number | null;
	practitionerId?: UserUuid | null;
	/**
	 * 1..43200 (30 days). Omitted means "the clinic's own `booking_link_expiry_days`" — there is
	 * no "never expires" path on this endpoint, unlike `issueFormLink`.
	 */
	expiresInMinutes?: number;
}

export const issueBookingLink = async (input: IssueBookingLinkInput): Promise<IssuedBookingLink> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/appointments/booking-links`,
		{
			patient_id: input.patientId,
			// axios drops undefined keys on serialisation, so an unscoped link simply omits them.
			appointment_type_id: input.appointmentTypeId ?? undefined,
			practitioner_id: input.practitionerId ?? undefined,
			expires_in_minutes: input.expiresInMinutes,
		}
	);

	return response.data;
};

/** That patient's links, newest first, capped server-side at 50. `patient_id` is required. */
export const listBookingLinks = async (patientId: UserUuid): Promise<BookingLink[]> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/appointments/booking-links`, {
		params: { patient_id: patientId },
	});

	return response.data?.data || [];
};

/** Idempotent — a second call does not move `revoked_at` and writes no second audit line. */
export const revokeBookingLink = async (id: number): Promise<BookingLink> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/appointments/booking-links/${id}/revoke`,
		{}
	);

	return response.data?.data;
};

